import type { Howl, HowlClass } from '@/hooks/audio/useAudio'

import { useHowler } from '@/hooks/audio/useAudio'


import { useRef, useState, useEffect } from 'react';
/**
 * 全局声音播放器，始终只维护一个 Howl 实例。
 *
 * - 点击新声音：销毁旧实例 → 创建新实例 → 加载完成自动播放
 * - 点击同一个声音：
 *   - 播放中 → 暂停
 *   - 暂停中 → 继续播放
 *   - 播放结束 → 从头播放
 */
export function useVoicePlayer() {
  const howlClassRef = useRef<HowlClass>()
  const howlRef = useRef<Howl>()

  const [currentVoiceId, setCurrentVoiceId] = useState<string | null>(null)
  const [currentSourceId, setCurrentSourceId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [playing, setPlaying] = useState(false)

  // 懒加载 Howler 库
  useHowler((Howl) => {
    howlClassRef.current = Howl
  })

  const destroyCurrentHowl = () => {
    if (howlRef.current) {
      howlRef.current.unload()
      howlRef.current = undefined
    }
    setPlaying(false)
    setLoading(false)
  }

  const createAndPlay = (src: string) => {
    const HowlCtor = howlClassRef.current
    if (!HowlCtor) return

    setLoading(true)
    const howl = new HowlCtor({
      src,
      html5: true,
      loop: false,
    })

    howl.on('load', () => {
      setLoading(false)
      howl.play()
    })

    howl.on('loaderror', () => {
      setLoading(false)
    })

    howl.on('play', () => setPlaying(true))
    howl.on('pause', () => setPlaying(false))
    howl.on('end', () => setPlaying(false))
    howl.on('stop', () => setPlaying(false))

    howlRef.current = howl
  }

  const play = (voiceId: string, src: string, sourceId: string) => {
    if (!src) return

    if (
      voiceId === currentVoiceId &&
      sourceId === currentSourceId &&
      howlRef.current
    ) {
      // 同一来源、同一声音：切换播放/暂停
      const howl = howlRef.current
      if (howl.playing()) {
        howl.pause()
      } else {
        howl.play()
      }
    } else {
      // 不同声音或不同来源：销毁旧实例，创建新实例
      destroyCurrentHowl()
      setCurrentVoiceId(voiceId)
      setCurrentSourceId(sourceId)
      createAndPlay(src)
    }
  }

  const stop = () => {
    destroyCurrentHowl()
    setCurrentVoiceId(null)
    setCurrentSourceId(null)
  }

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      howlRef.current?.unload()
    }
  }, [])

  return {
    currentVoiceId,
    currentSourceId,
    isPlaying: playing,
    loading,
    play,
    stop,
  }
}
